// KarmaNumber Component
// Отображение числа кармы (13, 14, 16, 19) с AI-интерпретацией

import React from 'react';

interface KarmaInterpretation {
  number: number;
  title: string;
  description: string;
  lessons: string[];
  challenges: string[];
  strengths: string[];
  recommendations: string[];
}

interface KarmaNumberData {
  karmaNumbers: number[];
  hasKarmicDebt: boolean;
  interpretations: KarmaInterpretation[];
  aiInterpretation?: string;
}

interface KarmaNumberProps {
  data: KarmaNumberData;
  loading?: boolean;
}

// Стили
const KARMIC_COLORS: Record<number, string> = {
  13: '#7B2D8E',
  14: '#8B3A9E',
  16: '#6B1D7E',
  19: '#5B0D6E',
};

const cardStyle: React.CSSProperties = {
  background: 'rgba(13, 25, 48, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(74, 20, 140, 0.3)',
  borderRadius: '20px',
  padding: '24px',
  marginBottom: '20px',
};

const karmicNumberStyle: React.CSSProperties = {
  fontSize: 'clamp(48px, 8vw, 72px)',
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  fontWeight: 700,
  textAlign: 'center',
  margin: '16px 0',
  textShadow: '0 0 30px rgba(123, 45, 142, 0.5)',
};

const sectionTitleStyle: React.CSSProperties = {
  color: '#9B59B6',
  fontSize: '16px',
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  fontWeight: 600,
  marginBottom: '12px',
  marginTop: '20px',
};

const textStyle: React.CSSProperties = {
  color: '#e0e0e0',
  fontSize: '14px',
  lineHeight: 1.7,
  fontFamily: 'var(--font-sans)',
  marginBottom: '8px',
};

const listItemStyle: React.CSSProperties = {
  color: '#d0d0d0',
  fontSize: '14px',
  lineHeight: 1.6,
  fontFamily: 'var(--font-sans)',
  paddingLeft: '20px',
  marginBottom: '6px',
  position: 'relative' as const,
};

const bulletStyle: React.CSSProperties = {
  position: 'absolute' as const,
  left: '4px',
  color: '#9B59B6',
};

const aiTextStyle: React.CSSProperties = {
  color: '#e0e0e0',
  fontSize: '14px',
  lineHeight: 1.7,
  fontFamily: 'var(--font-sans)',
  whiteSpace: 'pre-wrap',
  padding: '16px',
  background: 'rgba(74, 20, 140, 0.1)',
  borderRadius: '12px',
  border: '1px solid rgba(74, 20, 140, 0.2)',
};

const KarmaNumber: React.FC<KarmaNumberProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <p style={{ color: '#888' }}>Расчёт числа кармы...</p>
      </div>
    );
  }

  // Нет кармического долга
  if (!data.hasKarmicDebt) {
    return (
      <div>
        <div style={{ ...cardStyle, textAlign: 'center', borderColor: 'rgba(125, 211, 192, 0.3)' }}>
          <h3 style={{
            color: '#7DD3C0',
            fontSize: '20px',
            fontFamily: 'var(--font-display)',
            fontStyle: 'italic',
            marginBottom: '8px',
          }}>
            ✨ Кармического долга нет
          </h3>
          <div style={{
            fontSize: '48px',
            marginBottom: '16px',
          }}>
            🌟
          </div>
          <p style={{
            color: '#e0e0e0',
            fontSize: '15px',
            lineHeight: 1.7,
            fontFamily: 'var(--font-sans)',
          }}>
            Поздравляем! В вашей нумерологической карте нет кармических чисел (13, 14, 16, 19).
            Это означает, что у вас нет нерешённых задач из прошлых жизней, и вы можете
            сосредоточиться на развитии своих талантов и потенциала в этой жизни.
          </p>
          <p style={{
            color: '#7DD3C0',
            fontSize: '14px',
            fontFamily: 'var(--font-sans)',
            fontStyle: 'italic',
            marginTop: '16px',
          }}>
            Используйте эту энергию для создания своей лучшей жизни!
          </p>
        </div>

        {data.aiInterpretation && (
          <div style={cardStyle}>
            <h4 style={{
              color: '#9B59B6',
              fontSize: '16px',
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              marginBottom: '12px',
            }}>
              ✨ Интерпретация
            </h4>
            <div style={aiTextStyle}>
              {data.aiInterpretation}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Есть кармический долг
  return (
    <div>
      {/* Кармические числа */}
      {data.karmaNumbers.map((kn, idx) => {
        const interp = data.interpretations.find(i => i.number === kn);
        const color = KARMIC_COLORS[kn] || '#7B2D8E';

        return (
          <div key={idx} style={{ ...cardStyle, borderColor: `${color}44` }}>
            <h3 style={{
              color,
              fontSize: '18px',
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              marginBottom: '8px',
              textAlign: 'center',
            }}>
              🔮 {interp?.title || `Карма ${kn}`}
            </h3>

            {/* Большое число */}
            <div style={{
              ...karmicNumberStyle,
              color,
            }}>
              {kn}
            </div>

            {/* Описание */}
            {interp && (
              <>
                <p style={textStyle}>{interp.description}</p>

                {/* Уроки */}
                <div style={sectionTitleStyle}>📚 Уроки этой жизни:</div>
                {interp.lessons.map((lesson, i) => (
                  <div key={i} style={listItemStyle}>
                    <span style={bulletStyle}>•</span>
                    {lesson}
                  </div>
                ))}

                {/* Вызовы */}
                <div style={sectionTitleStyle}>⚡ Вызовы:</div>
                {interp.challenges.map((challenge, i) => (
                  <div key={i} style={{ ...listItemStyle, color: '#FFAA44' }}>
                    <span style={{ ...bulletStyle, color: '#FFAA44' }}>•</span>
                    {challenge}
                  </div>
                ))}

                {/* Сильные стороны */}
                <div style={sectionTitleStyle}>💪 Сильные стороны:</div>
                {interp.strengths.map((strength, i) => (
                  <div key={i} style={{ ...listItemStyle, color: '#7DD3C0' }}>
                    <span style={{ ...bulletStyle, color: '#7DD3C0' }}>•</span>
                    {strength}
                  </div>
                ))}

                {/* Рекомендации */}
                <div style={sectionTitleStyle}>🌟 Рекомендации:</div>
                {interp.recommendations.map((rec, i) => (
                  <div key={i} style={listItemStyle}>
                    <span style={bulletStyle}>→</span>
                    {rec}
                  </div>
                ))}
              </>
            )}
          </div>
        );
      })}

      {/* AI-интерпретация */}
      {data.aiInterpretation && (
        <div style={cardStyle}>
          <h4 style={{
            color: '#9B59B6',
            fontSize: '16px',
            fontFamily: 'var(--font-display)',
            fontStyle: 'italic',
            marginBottom: '12px',
          }}>
            🔮 Интерпретация
          </h4>
          <div style={aiTextStyle}>
            {data.aiInterpretation}
          </div>
        </div>
      )}
    </div>
  );
};

export default KarmaNumber;
